/**
 * Shared formatting helpers for the Statistics page and the chart kit.
 *
 * These were previously duplicated three times — private copies lived in
 * Statistics.js (fmtInt/fmtCompact/parseTs/fmtDate/fmtRelative), PerfCharts.js
 * (fmt/formatAgeShort/formatAbsolute/formatEventTime) and History.js
 * (formatDate/formatTime/formatDuration). Every copy drifted slightly; this is
 * the single home. `compactNumber` in formatNumber.js stays where it is — it
 * serves the map HUDs and has its own "k/m/b" convention.
 *
 * Convention throughout: a missing value renders as an em-dash, never as 0.
 * Showing 0 for "we don't know yet" is a lie the old stat cards told.
 */

import { convertDistance, UNIT_SYSTEMS } from './mapStyles';

export const EMPTY = '—';

/** Whole number with thousands separators. 1743 → "1,743". */
export function fmtInt(n) {
  if (n == null || Number.isNaN(Number(n))) return EMPTY;
  return Math.round(Number(n)).toLocaleString();
}

/** Compact number for chart axes and dense tiles. 1200 → "1.2k", 3.4e6 → "3.4M". */
export function fmtCompact(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  const v = Number(n);
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M';
  if (a >= 1e3) return (v / 1e3).toFixed(a >= 1e4 ? 0 : 1) + 'k';
  return Math.round(v).toString();
}

/** Fixed-decimal number, em-dash when absent. */
export function fmtNum(n, digits = 1) {
  if (n == null || Number.isNaN(Number(n))) return EMPTY;
  return Number(n).toFixed(digits);
}

/** Percentage from a 0-100 value. */
export function fmtPercent(n, digits = 1) {
  if (n == null || Number.isNaN(Number(n))) return EMPTY;
  return Number(n).toFixed(digits) + '%';
}

/**
 * Distance held internally in km, rendered in the user's unit system.
 * Returns { value, unit } so callers can style the unit separately.
 */
export function fmtDistance(km, unitSystem = 'metric', digits = 0) {
  const unit = (UNIT_SYSTEMS[unitSystem] || UNIT_SYSTEMS.metric).distance;
  if (km == null || Number.isNaN(Number(km)) || Number(km) <= 0) {
    return { value: EMPTY, unit };
  }
  const converted = convertDistance(Number(km), unitSystem);
  return { value: converted.toFixed(digits), unit };
}

/**
 * Parse a backend timestamp to a Date. The backend serialises LocalDateTime
 * without a zone ("2026-08-19T07:15:00"); those are UTC, so append Z when the
 * string carries no offset — otherwise the browser reads them as local time and
 * every "x minutes ago" is wrong by the timezone offset.
 */
export function parseTs(ts) {
  if (!ts) return null;
  if (typeof ts === 'number') {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = ts.includes('T') ? ts : ts.replace(' ', 'T');
  const withZone = /Z|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z';
  const d = new Date(withZone);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Locale date, e.g. "19/08/2026". */
export function fmtDate(ts) {
  const d = parseTs(ts);
  return d ? d.toLocaleDateString() : EMPTY;
}

/** Locale date + time, for "last active" style fields. */
export function fmtDateTime(ts) {
  const d = parseTs(ts);
  if (!d) return EMPTY;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** "5 min ago" style relative time. */
export function fmtRelative(ts) {
  const d = parseTs(ts);
  if (!d) return EMPTY;
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 0) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

/** Short age from a second count: "45 s", "12 min", "3 h", "2 d". */
export function fmtAgeShort(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return EMPTY;
  const s = Math.max(0, Math.floor(Number(seconds)));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return `${Math.floor(s / 86400)} d`;
}

/** Absolute local timestamp "2026-08-19 07:15:00". */
export function fmtAbsolute(ms) {
  if (ms == null || Number.isNaN(Number(ms))) return EMPTY;
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return EMPTY;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
    + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Day count with its unit: 18 → "18 days". */
export function fmtDays(n) {
  if (n == null || Number.isNaN(Number(n)) || Number(n) <= 0) return EMPTY;
  const d = Math.round(Number(n));
  return `${d} day${d === 1 ? '' : 's'}`;
}
